import {withDescendants, tree} from "./scoreHierarchy";


/**
 * recursively traverses components tree and returns flat array with all components (whatever depth)
 * @param components - array of components
 *  e.g. [{id: "foo", components: [{id: "foo.bar", components: [...]}]}]
 * @returns array of components
 */
export const flattenScoreComponentsTree = components => {
  return components.reduce((sum, x) => {
    return sum.concat([x]).concat(flattenScoreComponentsTree(x.components || []))
  }, [])
}

/**
 * Maps config component object to score item for reweight
 * @param {{id: String, value: Number}}
 * @return {{scorePath: String, value: Number}}
 */

export const scoreComponentToScoreItem = component =>
  ({scorePath: component.id, value: component.weight !== undefined ? component.weight / 100 : undefined })


export const reweightAllSync = (locationScores, weights, scorePathNames) => {
  const reweighted = locationScores.map(x => reweightLocationScores(x, weights))
  return avgScoresSync(reweighted, scorePathNames)
}


export const avgScoresSync = (scores, scorePathNames) => {
  let names = (scorePathNames || withDescendants('total'));

  // map of score path to all its values
  const subScores = names.reduce((sum, name) => ({...sum, [name]: []}), {})

  for (let locationWeights of scores) {
    for (let {scorePath, value} of locationWeights) {
      if (scorePath in subScores && value !== undefined) {
        subScores[scorePath].push(value)
      }
    }
  }

  return names.map(scorePath => ({scorePath, value: avg(...subScores[scorePath]) }))
}


/**
 *
 * @param scores - {Array.<{scorePath: String, [value: Number]}>} score items
 * @param weights - {Array.<{scorePath: String, value: Number}>}
 * @returns array of revalued score items for a location
 */
export const reweightLocationScores = (scores, weights) => {
  function toMap(array) {
    return array.reduce((hash, { scorePath, value }) => ({...hash, [scorePath]: value }), {})
  }
  const weightHash = toMap(weights)

  function weightedAverage(arrayOfValueWeights) {
    // doesn't include the weight if value is undefined
    const hasValue = arrayOfValueWeights.filter(x => x.value !== undefined && x.weight)

    const totalWeight = hasValue.reduce((sum, x) => sum + x.weight, 0)
    const totalValue = hasValue.reduce((sum, x) => sum + (x.value * x.weight), 0)
    return totalWeight > 0 ? (totalValue / totalWeight) : undefined
  }

  const recalc = (scoreItems, parentNodeName) => {
    const subNames = tree[parentNodeName]
    const subItems = scoreItems.filter(x => subNames.indexOf(x.scorePath) > -1)
    let valueWeights = subItems.map(x => {
      return { value: x.value, weight: weightHash[x.scorePath] || 0 }
    });
    const sum = weightedAverage(valueWeights)

    return {
      scoreItem:  {scorePath: parentNodeName, value: sum },
      descendants: subItems
    }
  }

  const recalcedSubcategories = tree['total'].reduce((sum, sub) => {
    const {scoreItem, descendants} = recalc(scores, sub)
    return sum.concat([scoreItem]).concat(descendants)
  }, [])

  const totalRecalc = recalc(recalcedSubcategories, 'total').scoreItem

  let out = [totalRecalc].concat(recalcedSubcategories);
  return out
}



function avg(...args) {
  if (args.length){
    return args.reduce((a, b) => a + b, 0) / args.length
  }
}
